import { BufferManager } from './buffer-manager.ts'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { hashFloat32Array, MAX_SAMPLE_LENGTH, type SequenceData } from './bytecode.ts'
import { ImpulseResponseLoader } from './impulse-response-loader.ts'
import { rpc } from './lib/rpc.ts'
import { SamLoader } from './sam-loader.ts'
import { SampleLoader } from './sample-loader.ts'

interface SampleData {
  sampleId: number
  data: ArrayBuffer
  length: number
  contentHash: number
}

interface IRData {
  irId: number
  data: ArrayBuffer
  length: number
}

interface SingleProgramData {
  bytecode: Float64Array
  sequences: { data: Float64Array; slotIndex: number }[]
  samples: SampleData[]
  impulseResponses: IRData[]
  bpm: number
  hasExplicitBpm: boolean
  tune: number
}

interface ExportAudioOptions {
  code: string
  globalBpm: number
  bars: number
  oversampling: 1 | 2 | 4 | 8
  title?: string
  artist?: string
}

interface ExportAudioResult {
  url: string
  filename: string
}

export async function exportAudioFromCode({
  code,
  globalBpm,
  bars,
  oversampling,
  title,
  artist,
}: ExportAudioOptions): Promise<ExportAudioResult> {
  const compiler = new BytecodeCompiler()

  try {
    const program = compiler.compile(code, globalBpm)
    const bpm = program.bpm

    const beatsPerBar = 4
    const seconds = (bars * beatsPerBar * 60) / bpm
    const sampleRate = 48000

    const offlineContext = new OfflineAudioContext({
      numberOfChannels: 2,
      length: Math.ceil(sampleRate * seconds),
      sampleRate,
    })

    const workletUrl = import.meta.env.PROD && (window as any).__WORKLET_PATH__
      ? (window as any).__WORKLET_PATH__
      : '/src/processor-wasm.ts'
    await offlineContext.audioWorklet.addModule(workletUrl)

    const workletNode = new AudioWorkletNode(offlineContext, 'dspscript-processor', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: {
        sourcemapUrl: new URL('/as/build/index.wasm.map', location.origin).toString(),
      },
    })

    workletNode.connect(offlineContext.destination)

    const offlineBufferManager = new BufferManager()
    offlineBufferManager.setOversampling(oversampling)

    type OfflineWorkletRpc = {
      init: (buffers: any) => Promise<{ wasmMemory?: SharedArrayBuffer }>
      loadWasm: (binary: ArrayBuffer) => Promise<{ wasmMemory: SharedArrayBuffer }>
      loadNewProgram: (data: SingleProgramData) => Promise<void>
      loadIR: (irId: number, irData: ArrayBuffer, length: number) => Promise<void>
      loadSample: (
        sampleId: number,
        sampleData: ArrayBuffer,
        length: number,
        contentHash: number,
      ) => Promise<void>
      start: () => Promise<void>
      stop: () => Promise<void>
    }

    const offlineRpc = rpc<OfflineWorkletRpc>(workletNode.port, {})

    await offlineRpc.init(offlineBufferManager.getSharedBuffers())

    const wasmResponse = await fetch('/as/build/index.wasm?t=' + Date.now())
    const wasmBinary = await wasmResponse.arrayBuffer()
    await offlineRpc.loadWasm(wasmBinary)

    const irLoader = new ImpulseResponseLoader(offlineContext as any as AudioContext)
    const sampleLoader = new SampleLoader(offlineContext as any as AudioContext)
    const samLoader = new SamLoader(offlineContext as any as AudioContext)

    if (program.impulseResponses.length > 0) {
      const impulseResponses = [...new Set(program.impulseResponses)]
      for (const irPath of impulseResponses) {
        try {
          const irData = await irLoader.loadImpulseResponse(irPath)
          const channelData = irData.audioBuffer.getChannelData(0)
          const length = channelData.length
          const buffer = new Float32Array(length)
          buffer.set(channelData)
          await offlineRpc.loadIR(irData.id, buffer.buffer, length)
          await new Promise(resolve => setTimeout(resolve, 50))
        }
        catch {}
      }
    }

    if (program.samplePaths.length > 0) {
      for (let sampleId = 0; sampleId < program.samplePaths.length; sampleId++) {
        const samplePath = program.samplePaths[sampleId]

        if (samplePath.startsWith('__SAM__')) {
          continue
        }

        try {
          const sampleData = await sampleLoader.loadSample(samplePath)
          const channelData = sampleData.audioBuffer.getChannelData(0)
          const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
          const buffer = channelData.slice(0, length)
          const truncatedHash = hashFloat32Array(buffer)
          await offlineRpc.loadSample(sampleId, buffer.buffer, length, truncatedHash)
          await new Promise(resolve => setTimeout(resolve, 50))
        }
        catch {
          try {
            const emptyBuffer = new Float32Array(0)
            await offlineRpc.loadSample(sampleId, emptyBuffer.buffer, 0, -1)
          }
          catch {}
        }
      }
    }

    if (program.samSpeeches.length > 0) {
      for (let sampleId = 0; sampleId < program.samplePaths.length; sampleId++) {
        const samplePath = program.samplePaths[sampleId]
        if (samplePath.startsWith('__SAM__')) {
          const samHashStr = samplePath.replace('__SAM__', '')
          const samHash = parseInt(samHashStr, 10)
          const params = program.samSpeeches.find(([hash]) => hash === samHash)?.[1]
          if (!params) continue

          try {
            const samData = await samLoader.generateSpeech(params)
            const channelData = samData.audioBuffer.getChannelData(0)
            const length = Math.min(channelData.length, MAX_SAMPLE_LENGTH)
            const buffer = channelData.slice(0, length)
            await offlineRpc.loadSample(sampleId, buffer.buffer, length, samData.id)
            await new Promise(resolve => setTimeout(resolve, 50))
          }
          catch {}
        }
      }
    }

    const serializeSequence = (seq: SequenceData): Float64Array => {
      let totalSize = 6 // Added 1 for cycleLength
      for (const note of seq.notes) {
        totalSize += 5 + note.pitches.length
      }

      const buffer = new Float64Array(totalSize)
      let offset = 0

      buffer[offset++] = seq.id
      buffer[offset++] = seq.notes.length
      buffer[offset++] = seq.swing ?? 0
      buffer[offset++] = seq.offset ?? 0
      buffer[offset++] = seq.exponent ?? 1
      buffer[offset++] = seq.cycleLength ?? 0

      for (const note of seq.notes) {
        buffer[offset++] = note.pitches.length
        for (let j = 0; j < note.pitches.length; j++) {
          buffer[offset++] = note.pitches[j]
        }
        buffer[offset++] = note.duration
        buffer[offset++] = note.velocity
        buffer[offset++] = note.hold
        buffer[offset++] = note.glide ?? 0
      }

      return buffer
    }

    const programData: SingleProgramData = {
      bytecode: program.bytecode || new Float64Array(0),
      sequences: (program.sequences || []).map((seq, index) => ({
        data: serializeSequence(seq),
        slotIndex: index,
      })),
      samples: [],
      impulseResponses: [],
      bpm: program.bpm,
      hasExplicitBpm: program.hasExplicitBpm,
      tune: program.tune,
    }

    await offlineRpc.loadNewProgram(programData)

    offlineBufferManager.loadProgram(program)
    offlineBufferManager.prepareWorkletLoad()
    offlineBufferManager.setOversampling(oversampling)

    await new Promise(resolve => setTimeout(resolve, 300))
    offlineBufferManager.start()
    await offlineRpc.start()

    const renderedBuffer = await offlineContext.startRendering()

    const declickDuration = 0.05
    const declickSamples = Math.floor(sampleRate * declickDuration)
    const declickedBuffer = applyDeclick(renderedBuffer, declickSamples)

    const wav = audioBufferToWav(declickedBuffer)
    const blob = new Blob([wav], { type: 'audio/wav' })
    const url = URL.createObjectURL(blob)

    const filename = (() => {
      const hasTitle = title && title !== 'Untitled'
      const hasArtist = artist && artist.trim() !== ''
      if (hasArtist && hasTitle) {
        return `${artist} - ${title}.wav`
      }
      if (hasTitle) {
        return `${title}.wav`
      }
      return 'export.wav'
    })()

    return { url, filename }
  }
  catch (err: any) {
    throw new Error(err?.message || 'Failed to export audio')
  }
}

const applyDeclick = (buffer: AudioBuffer, declickSamples: number): AudioBuffer => {
  const sampleRate = buffer.sampleRate
  const numberOfChannels = buffer.numberOfChannels
  const length = buffer.length

  if (length <= declickSamples) {
    return buffer
  }

  const newBuffer = new AudioContext().createBuffer(numberOfChannels, length, sampleRate)

  for (let channel = 0; channel < numberOfChannels; channel++) {
    const originalData = buffer.getChannelData(channel)
    const newData = newBuffer.getChannelData(channel)

    newData.set(originalData)

    for (let i = 0; i < declickSamples; i++) {
      const fadeOut = 1 - (i / declickSamples)
      const endIndex = length - declickSamples + i
      newData[endIndex] = originalData[endIndex]! * fadeOut
    }
  }

  return newBuffer
}

const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
  const numberOfChannels = buffer.numberOfChannels
  const length = buffer.length * numberOfChannels * 2
  const arrayBuffer = new ArrayBuffer(44 + length)
  const view = new DataView(arrayBuffer)
  const channels: Float32Array[] = []
  let offset = 0
  let pos = 0

  const setUint16 = (data: number) => {
    view.setUint16(pos, data, true)
    pos += 2
  }
  const setUint32 = (data: number) => {
    view.setUint32(pos, data, true)
    pos += 4
  }

  setUint32(0x46464952)
  setUint32(36 + length)
  setUint32(0x45564157)

  setUint32(0x20746d66)
  setUint32(16)
  setUint16(1)
  setUint16(numberOfChannels)
  setUint32(buffer.sampleRate)
  setUint32(buffer.sampleRate * numberOfChannels * 2)
  setUint16(numberOfChannels * 2)
  setUint16(16)

  setUint32(0x61746164)
  setUint32(length)

  for (let i = 0; i < numberOfChannels; i++) {
    channels.push(buffer.getChannelData(i))
  }

  while (pos < arrayBuffer.byteLength) {
    for (let i = 0; i < numberOfChannels; i++) {
      let sample = channels[i]![offset]!
      sample = Math.max(-1, Math.min(1, sample))
      view.setInt16(pos, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      pos += 2
    }
    offset++
  }

  return arrayBuffer
}
